/**
 * Privacy Policy Page
 * SaccoTeck — standalone route, linked from the landing page footer.
 * Styling matches LoginPage.jsx's design system (navy / gold / cream, Playfair Display headings).
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';

const NAVY = '#0D1B2A';
const GOLD = '#C9A84C';
const CREAM = '#F8F5F0';
const MUTED = '#6B7280';
const BORDER = '#e2d9cc';

const sectionStyle = { marginBottom: 32 };
const h2Style = {
  fontFamily: "'Playfair Display', serif",
  fontSize: 20,
  color: NAVY,
  marginBottom: 12,
  paddingBottom: 8,
  borderBottom: `2px solid ${BORDER}`,
};
const pStyle = { fontSize: 15, color: '#374151', lineHeight: 1.75, marginBottom: 14 };
const ulStyle = { paddingLeft: 22, marginBottom: 14 };
const liStyle = { fontSize: 15, color: '#374151', lineHeight: 1.75, marginBottom: 8 };

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();

  return (
    <div style={{ background: CREAM, minHeight: '100vh' }}>
      {/* ── HEADER (matches landing page nav bar) ── */}
      <div style={{
        background: NAVY, padding: '20px 6%', display: 'flex',
        alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ cursor: 'pointer' }} onClick={() => navigate('/')}>
          <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 700, color: GOLD }}>
            SaccoTeck
          </span>
          <div style={{ fontSize: 10, color: '#9ca3af', letterSpacing: 1 }}>BY JEPROS TECK</div>
        </div>
        <button
          onClick={() => navigate('/')}
          style={{
            background: 'transparent', color: '#fff', border: `1px solid ${GOLD}`,
            padding: '10px 20px', borderRadius: 4, fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}
        >
          ← Back to Home
        </button>
      </div>

      {/* ── CONTENT ── */}
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '56px 24px 90px' }}>
        <p style={{ fontSize: 12, letterSpacing: 3, textTransform: 'uppercase', color: GOLD, marginBottom: 12 }}>
          Legal
        </p>
        <h1 style={{
          fontFamily: "'Playfair Display', serif", fontSize: 'clamp(30px, 4vw, 42px)',
          color: NAVY, marginBottom: 8, lineHeight: 1.15,
        }}>
          Privacy Policy
        </h1>
        <p style={{ fontSize: 13, color: MUTED, marginBottom: 40 }}>
          SaccoTeck Member Portal · Last updated: September 2026
        </p>

        <div style={{
          background: '#fff', borderRadius: 12, padding: '40px 36px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        }}>

          <div style={sectionStyle}>
            <h2 style={h2Style}>1. Introduction</h2>
            <p style={pStyle}>
              SaccoTeck ("we," "our," or "us") provides the SaccoTeck Member Portal, including its
              web platform and mobile application (together, the "Service"), which allows SACCO
              members and administrators to manage member accounts, loans, shares, and financial
              reporting. This Privacy Policy explains what information we collect, how we use it,
              and the choices you have regarding your information.
            </p>
            <p style={pStyle}>
              By creating an account or using the Service, you agree to the collection and use of
              information as described in this policy.
            </p>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>2. Information We Collect</h2>
            <p style={pStyle}>To provide the Service, we collect the following categories of information:</p>
            <ul style={ulStyle}>
              <li style={liStyle}><strong>Identity information:</strong> full name, national ID number, date of birth.</li>
              <li style={liStyle}><strong>Contact information:</strong> phone number, email address, physical address.</li>
              <li style={liStyle}><strong>Employment information:</strong> employment status and employer name, where provided.</li>
              <li style={liStyle}><strong>Next of kin details:</strong> name, relationship, phone number, ID number, and address of a designated next of kin.</li>
              <li style={liStyle}><strong>Financial and account information:</strong> share balances, loan balances, loan repayment history, transaction and deduction records, and account statements.</li>
              <li style={liStyle}><strong>Authentication data:</strong> login credentials and, where applicable, one-time passcodes (OTP) sent to your registered phone number to verify your identity.</li>
              <li style={liStyle}><strong>Device and usage information:</strong> basic technical information such as device type and app version, used for troubleshooting and security purposes.</li>
            </ul>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>3. How We Use Your Information</h2>
            <ul style={ulStyle}>
              <li style={liStyle}>Create and manage member and administrator accounts.</li>
              <li style={liStyle}>Display share balances, loan balances, and account statements.</li>
              <li style={liStyle}>Verify identity during login using OTP-based authentication.</li>
              <li style={liStyle}>Process and evaluate loan eligibility and loan requests.</li>
              <li style={liStyle}>Generate account statements and financial reports.</li>
              <li style={liStyle}>Communicate regarding your account, including security notices.</li>
              <li style={liStyle}>Maintain the security and integrity of the Service and investigate suspicious activity.</li>
              <li style={liStyle}>Comply with applicable legal and regulatory obligations relevant to SACCO recordkeeping.</li>
            </ul>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>4. How We Store and Protect Your Information</h2>
            <p style={pStyle}>
              Information is stored using Google Firebase infrastructure, which provides encryption
              of data in transit and at rest. Access to member data is restricted to authorized
              personnel who require it to operate and maintain the Service. Authentication is
              protected using phone number OTP verification in addition to your password.
            </p>
            <p style={pStyle}>
              While we take reasonable technical and organizational measures to protect your
              information, no method of electronic transmission or storage is completely secure,
              and we cannot guarantee absolute security.
            </p>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>5. Sharing of Information</h2>
            <p style={pStyle}>We do not sell your personal information. We may share information only in the following circumstances:</p>
            <ul style={ulStyle}>
              <li style={liStyle}><strong>Service providers:</strong> with infrastructure providers such as Google Firebase, which processes data on our behalf.</li>
              <li style={liStyle}><strong>Legal requirements:</strong> where required to comply with a legal obligation, court order, or regulatory request.</li>
              <li style={liStyle}><strong>SACCO administration:</strong> with authorized SACCO staff for account management, loan processing, and member services.</li>
            </ul>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>6. Data Retention</h2>
            <p style={pStyle}>
              We retain member information for as long as your SACCO membership is active, and for
              a reasonable period afterward as required for recordkeeping, auditing, and legal
              compliance purposes.
            </p>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>7. Your Rights</h2>
            <p style={pStyle}>Subject to applicable law, you have the right to:</p>
            <ul style={ulStyle}>
              <li style={liStyle}>Access the personal information we hold about you.</li>
              <li style={liStyle}>Request correction of inaccurate or incomplete information (note: some profile fields, such as full name and Member ID, cannot be self-edited and require SACCO administrator assistance).</li>
              <li style={liStyle}>Request deletion of your information, subject to our legal and regulatory recordkeeping obligations as a SACCO.</li>
              <li style={liStyle}>Withdraw consent where processing is based on consent.</li>
            </ul>
            <p style={pStyle}>To exercise any of these rights, please contact us using the details below.</p>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>8. Children's Privacy</h2>
            <p style={pStyle}>
              The Service is intended for use by SACCO members and administrators who are adults.
              We do not knowingly collect information from individuals under the age of 18.
            </p>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>9. Changes to This Policy</h2>
            <p style={pStyle}>
              We may update this Privacy Policy from time to time. Changes will be posted on this
              page with an updated revision date. Continued use of the Service after changes are
              posted constitutes your acceptance of the revised policy.
            </p>
          </div>

          <div>
            <h2 style={h2Style}>10. Contact Us</h2>
            <p style={pStyle}>If you have questions or concerns about this Privacy Policy or how your information is handled, please contact us:</p>
            <div style={{ background: CREAM, border: `1px solid ${BORDER}`, borderRadius: 8, padding: '16px 20px' }}>
              <p style={{ ...pStyle, marginBottom: 4, fontWeight: 700, color: NAVY }}>SaccoTeck</p>
              <p style={{ ...pStyle, marginBottom: 4 }}>Email: jeprosservices@gmail.com</p>
              <p style={{ ...pStyle, marginBottom: 0 }}>Website: https://saccoteck.com</p>
            </div>
          </div>

        </div>

        <p style={{ textAlign: 'center', fontSize: 12, color: MUTED, marginTop: 32 }}>
          © {new Date().getFullYear()} Jepros Teck · Cable Systems Interprise
        </p>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
